import { useState, useMemo } from 'react';
import SiteLayout from '../layouts/SiteLayout';
import Header from '../components/Header/Header';
import { useScreenType } from '../hooks/useScreenType';
import { useFinancialProjections } from '../hooks/useFinancialProjections';
import { ProjectionFiltersState, FILTRES_PAR_DEFAUT } from './ProjectionsFilters';
import FinancialProjectionsDesktop from './FinancialProjectionsDesktop';
import FinancialProjectionsMobile from './FinancialProjectionsMobile';
import { ProjectionRow, StatusFilter, synthese, filtreStatut, estDivergent } from './projectionsModel';
import { SortConfig, SortDirection, SortKey } from './projectionsFormat';

import './FinancialProjections.css';

const FinancialProjections = () => {
    const { isMobile, isTablet } = useScreenType();
    const [keyword, setKeyword] = useState('');
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'yield_est', direction: 'desc' });
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [filters, setFilters] = useState<ProjectionFiltersState>(FILTRES_PAR_DEFAUT);
    const [anneeChoisie, setAnneeChoisie] = useState<number | null>(null);

    const { annees, annee, data, loading, error, cacheDate } = useFinancialProjections(anneeChoisie);

    const handleSort = (key: SortKey) => {
        const direction: SortDirection = sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc';
        setSortConfig({ key, direction });
    };

    const summaryMetrics = useMemo(() => (data.length ? synthese(data) : null), [data]);

    const processedData = useMemo(() => {
        let items = data.filter(item => filtreStatut(item, statusFilter));

        if (keyword) {
            const k = keyword.toLowerCase();
            items = items.filter(item => item.symbol.toLowerCase().includes(k));
        }
        if (filters.minYield > 0) items = items.filter(item => (item.yield_est ?? 0) >= filters.minYield);
        if (filters.maxPer !== null) items = items.filter(item => (item.per_est ?? 0) > 0 && (item.per_est as number) <= filters.maxPer!);
        if (filters.reliability !== 'any') items = items.filter(item => item.reliability_tier === filters.reliability);
        if (filters.riskOnly) items = items.filter(estDivergent);
        const hausse = (v: number | null) => (v ?? 0) > 0;
        switch (filters.growthTarget) {
            case 'div': items = items.filter(i => hausse(i.var_div)); break;
            case 'rev': items = items.filter(i => hausse(i.var_revenue)); break;
            case 'res': items = items.filter(i => hausse(i.var_net_income)); break;
            case 'all_growth': items = items.filter(i => hausse(i.var_div) && hausse(i.var_revenue) && hausse(i.var_net_income)); break;
        }

        const valeur = (row: ProjectionRow) => {
            const v = row[sortConfig.key];
            return typeof v === 'number' ? v : -Infinity;
        };
        return [...items].sort((a, b) => {
            const av = valeur(a);
            const bv = valeur(b);
            if (av === bv) return 0;
            return (av < bv ? -1 : 1) * (sortConfig.direction === 'asc' ? 1 : -1);
        });
    }, [data, keyword, sortConfig, filters, statusFilter]);

    const sharedProps = {
        data,
        loading,
        error,
        cacheDate,
        annee,
        annees,
        onAnneeChange: setAnneeChoisie,
        keyword,
        setKeyword,
        sortConfig,
        handleSort,
        statusFilter,
        setStatusFilter,
        filters,
        setFilters,
        processedData,
        summaryMetrics,
    };

    return (
        <SiteLayout>
            {!isMobile && !isTablet && <Header title={annee ? `Projections ${annee}` : 'Projections financières'} icon="trending_up" />}
            {(isMobile || isTablet)
                ? <FinancialProjectionsMobile {...sharedProps} />
                : <FinancialProjectionsDesktop {...sharedProps} />
            }
        </SiteLayout>
    );
};

export default FinancialProjections;
